import { createSlice } from '@reduxjs/toolkit';
import { capitalize } from 'lodash';
// utils
import { axiosInstanceDefault as axios } from '../../utils/axios';
//
import { dispatch, useSelector, store } from '../store';
import { GetAppointments, getAppointments, getAppointmentsByDoctor, getAppointmentsByPatient, getAppointmentsByTherapist, patchAppointment, postAppointment } from '../../api/appointment';
import { dateConfigDB } from '../../config';

// ----------------------------------------------------------------------

const initialState = {
  isLoading: false,
  error: null,
  events: [],
  isOpenModal: false,
  selectedEventId: null,
  selectedRange: null,
  selectedRoom: null,
  selectedBranch: null,
  myAppointmentsOnly: false
};

const slice = createSlice({
  name: 'calendar',
  initialState,
  reducers: {
    // START LOADING
    startLoading(state) {
      state.isLoading = true;
    },

    // HAS ERROR
    hasError(state, action) {
      state.isLoading = false;
      state.error = action.payload;
    },

    // CLEAR ERROR
    clearError(state) {
      state.error = null;
    },

    // GET EVENTS
    getEventsSuccess(state, action) {
      state.isLoading = false;
      state.events = action.payload;
      state.selectedEventId = null
    },

    // CREATE EVENT
    createEventSuccess(state, action) {
      // const newEvent = action.payload;
      // state.successMessage = 'Event created';
      // state.events = [...state.events, newEvent];
      // state.isLoading = false;
    },

    // UPDATE EVENT
    updateEventSuccess(state, action) {
      // const event = action.payload;
      // const updateEvent = state.events.map((_event) => {

      //   if (_event.id === event.id) {
      //     return event;
      //   }
      //   return _event;
      // });

      // state.successMessage = 'Event updated';
      // state.events = updateEvent;
      // state.isLoading = false;
    },

    // DELETE EVENT
    deleteEventSuccess(state, action) {
      const { eventId } = action.payload;
      const deleteEvent = state.events.filter((event) => event.id !== eventId);
      state.events = deleteEvent;
    },

    // SELECT EVENT
    selectEvent(state, action) {
      const eventId = action.payload;
      // state.isOpenModal = true;
      state.selectedEventId = eventId;
    },

    staffSelectEvent(state, action) {
      const eventId = action.payload;
      state.isOpenModal = true;
      state.selectedEventId = eventId;
    },
    roomSelectEvent(state, action) {
      const room = action.payload;
      if (room) state.selectedRoom = room;
    },
    branchSelectEvent(state, action) {
      const branch = action.payload;
      if (branch) {
        state.selectedBranch = branch;
        state.selectedRoom = 'tbd'
      }
    },
    onChangeMyAppointmentOnly(state, action) {
      state.myAppointmentsOnly = !!action.payload
    },

    // SELECT RANGE
    selectRange(state, action) {
      const { start, end } = action.payload;
      // state.isOpenModal = true;
      state.selectedRange = { start, end };
      state.selectedEventId = null
    },

    // OPEN MODAL
    openModal(state) {
      state.isOpenModal = true;
    },

    // CLOSE MODAL
    closeModal(state) {
      state.isOpenModal = false;
      state.selectedEventId = null;
      state.selectedRange = null;
    },
  },
});

// Reducer
export default slice.reducer;

// Actions
export const {
  openModal,
  closeModal,
  selectEvent,
  staffSelectEvent,
  roomSelectEvent,
  branchSelectEvent,
  onChangeMyAppointmentOnly,
} = slice.actions;

// ----------------------------------------------------------------------

function getAppointmentColor(status) {
  // pending color = yellow
  return status === 'pending' ? '#FFC107' :
    // blue color = complete
    status === 'completed' ? '#3CD4A0' :
      // red color = cancelled
      status === 'canceled' ? '#FF3739' :
        // green color = confirmed
        status === 'confirmed' ? '#4CAFFF' :
          // default color = red
          '#FF3739';
}

function formatEvent(event) {
  if (!(event.appointmentDate instanceof Date)) {
    event.appointmentDate = new Date(event.appointmentDate).toLocaleDateString(...dateConfigDB)
  } else {
    event.appointmentDate = event.appointmentDate.toLocaleDateString(...dateConfigDB)
  }

  // format date to YYYY-MM-DD
  const dateArr = event.appointmentDate.split('/') // [month, day, year]
  event.appointmentDate = `${dateArr[2]}-${dateArr[0]}-${dateArr[1]}`

  // appointmentDate + appointmentTime local time
  // pad appointment time to 24 hour format
  const appointmentStart = new Date(`${event.appointmentDate}T${event.appointmentTime.padStart(5, '0')}+08:00`)

  // start + 1 hours
  const appointmentEnd = new Date(appointmentStart.getTime() + 1 * 60 * 60 * 1000)
  // color: status === 'pending' ? '#FFC107' : '#3CD4A0',
  const color = getAppointmentColor(event.status)
  return {
    id: event._id,
    appointmentNumber: event?.appointmentNumber,
    reason: event.reason,
    title: event.reason ? `${event.reason} : ${capitalize(event.room).replace(':', ' ')}` : 'Booked',
    room: event?.room,
    requestedBranch: event?.meta?.requestedBranch,
    cancelReason: event?.meta?.cancelReason,
    // description: event.description,
    start: appointmentStart,
    end: appointmentEnd,
    doctorId: event?.doctorId?._id,
    therapistId: event?.therapistId?._id,
    patientId: event?.patientId?._id,
    // allDay: event.allDay,
    textColor: color,
    borderColor: color,
    backgroundColor: color,
    status: event.status,

    doctorDetails: event?.doctorId,
    therapistDetails: event?.therapistId,
    patientDetails: event?.patientId,
  }
}

export function getEvents(query) {
  // console.log('query', query)
  const queryParams = {}
  if (query?.status) queryParams.status = query?.status
  if (query?.doctorId) queryParams.doctorId = query?.doctorId
  if (query?.patientId) queryParams.patientId = query?.patientId
  if (query?.room) queryParams.room = query?.room
  if (query?.branch && query?.branch !== 'all') queryParams.branch = query?.branch
  if (query?.year_month) queryParams.year_month = query?.year_month

  let getAppointmentsFunc = getAppointments
  if (query?.isDoctor) getAppointmentsFunc = getAppointmentsByDoctor
  if (query?.isTherapist) getAppointmentsFunc = getAppointmentsByTherapist
  if (query?.isPatientOnly) getAppointmentsFunc = getAppointmentsByPatient

  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await getAppointmentsFunc(queryParams);
      const events = response.result?.map((event) => {
        const formattedEvent = formatEvent(event)
        return formattedEvent 
      })
      dispatch(slice.actions.getEventsSuccess(events));
    } catch (error) {
      console.error('error in getEvents', error)
      dispatch(slice.actions.hasError(error));
    }
  };
}

// ----------------------------------------------------------------------

export function createEvent(newEvent) {
  // return async () => {
  //   dispatch(slice.actions.startLoading());
  //   try {
  //     // const response = await axios.post('/api/calendar/events/new', newEvent);
  //     const { appointment, message } = await postAppointment(newEvent);
  //     const formattedEvent = formatEvent(appointment)
  //     dispatch(slice.actions.createEventSuccess(formattedEvent));
  //   } catch (error) {
  //     console.log('error in create event', error)
  //     dispatch(slice.actions.hasError(error));
  //   }
  // };
}

// ----------------------------------------------------------------------

export function updateEvent(eventId, updateEvent) {
  // return async () => {
  //   dispatch(slice.actions.startLoading());
  //   try {
  //     const { appointment, message } = await patchAppointment(eventId, updateEvent);
  //     const formattedEvent = formatEvent(appointment)
  //     dispatch(slice.actions.updateEventSuccess(formattedEvent));
  //   } catch (error) {
  //     dispatch(slice.actions.hasError(error));
  //   }
  // };
}

// ----------------------------------------------------------------------

export function deleteEvent(eventId) {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      await axios.post('/api/calendar/events/delete', { eventId });
      dispatch(slice.actions.deleteEventSuccess({ eventId }));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

// ----------------------------------------------------------------------

export function selectRange(start, end) {
  return async () => {
    dispatch(
      slice.actions.selectRange({
        start: start.getTime(),
        end: end.getTime(),
      })
    );
  };
}
